import { Component, ElementRef, ViewChild, Renderer2, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ScriptService } from 'src/app/services/script.service';

type EpagoFormInstance = { render: (selector: string) => void; unmount: () => void };

/** Bundle UMD fijo en QAS; no se edita en el formulario. */
const EPAGO_REACT_SDK_UMD_URL_QAS =
  'https://qas.pay.button.epago.pe/web/react-sdk-1.0.0/epago-sdk.umd.js';

@Component({ standalone: false,
  selector: 'app-form-checkout-react-qr',
  templateUrl: './form-checkout-react-qr.component.html',
  styleUrls: ['../form-checkout2/form-checkout2.component.css'],
})
export class FormCheckoutReactQrComponent implements OnInit, OnDestroy {
  @ViewChild('dialog', { static: true }) dialog: ElementRef<HTMLDialogElement> | undefined;
  @ViewChild('cartContainer', { static: true }) cartContainer: ElementRef | undefined;
  @ViewChild('cartContainer2', { static: true }) cartContainer2: ElementRef | undefined;

  myForm: FormGroup;
  minicartItems: FormGroup;
  items: any[] = [
    {
      name: 'Heladera',
      quantity: 1,
      unitAmount: 10,
      amount: 10,
      sku: '1234567890',
      category: 'Electrodoméstico',
      discount: 0,
      shipping: 0,
    },
  ];

  loadError: string | null = null;
  loadingSdk = false;
  private epagoFormInstance: EpagoFormInstance | null = null;
  /** Igual que form-checkout / form-checkout2: número de orden = epoch en segundos, actualizado cada segundo */
  private orderIdInterval: ReturnType<typeof setInterval> | null = null;

  constructor(
    private formBuilder: FormBuilder,
    private renderer: Renderer2,
    private scriptService: ScriptService,
  ) {
    const defaultOrderId = this.generateOrderId();
    this.myForm = this.formBuilder.group({
      env: ['QAV1'],
      sdkUrl: [{ value: EPAGO_REACT_SDK_UMD_URL_QAS, disabled: true }, Validators.required],
      merchantKey: ['OCC-40467ba5-f254-4cf0-937e-9f42a206cb3f', Validators.required],
      /** Secret key para firmar `POST orders/create` (mismo criterio que PayLink). */
      merchantToken: ['93ffbb5b3e574d76a89b43ded9daa0e4', Validators.required],
      /** Código de tienda del comercio (cuerpo `order.merchantStoreCode`). */
      merchantStoreCode: ['435ff670-b613-4853-a8d1-9032d661b162', Validators.required],
      orderId: [defaultOrderId, Validators.required],
      quantity: [1],
      sku: ['1020304005060'],
      currency: ['PEN', Validators.required],
      amount: [10.1],
      discounts: [0],
      shipping: [0],
      taxes: [0],
      fee: [0],
      email: ['test@test.com', Validators.required],
      name: ['Jhony', Validators.required],
      lastname: ['Pacheco', Validators.required],
      phone: ['998910021', Validators.required],
      identifier: ['a2324f1a-d527-4e3a-bcf4-aea3125f5f93', Validators.required],
      documentType: ['DNI', Validators.required],
      documentNumber: ['98910021', Validators.required],
      address1: ['Av Zeta 1', Validators.required],
      addressCountry: ['PER', Validators.required],
      addressLevel1: ['LIMA', Validators.required],
      addressLevel2: ['LIMA', Validators.required],
      addressLevel3: ['SAN ISIDRO', Validators.required],
      addressZip: ['051014', Validators.required],
      expirationDate: ['2029-12-31T23:59:59.999-0500', Validators.required],
      integrationMode: [null as string | null],
    });

    this.minicartItems = this.formBuilder.group({
      name: ['Heladera'],
      quantity: [1],
      unitAmount: [10],
      amount: [10],
      sku: ['1234567890'],
      category: ['Electrodoméstico'],
      discount: [0],
      shipping: [0],
    });

    this.myForm.get('amount')?.setValue(this.calculateTotal());
  }

  ngOnInit(): void {
    this.myForm.get('orderId')?.valueChanges.subscribe(() => {
      this.myForm.get('amount')?.setValue(this.calculateTotal());
    });

    this.orderIdInterval = setInterval(() => {
      const next = this.generateOrderId();
      this.myForm.get('orderId')?.setValue(next, { emitEvent: true });
    }, 1000);
  }

  ngOnDestroy(): void {
    if (this.orderIdInterval != null) {
      clearInterval(this.orderIdInterval);
      this.orderIdInterval = null;
    }
    this.teardownForm();
  }

  /** Timestamp Unix en segundos (mismo criterio que los otros modos del demo). */
  generateOrderId(): string {
    return String(Math.floor(Date.now() / 1000));
  }

  calculateTotal(): number {
    let total = 0;
    if (this.items.length > 0) {
      total = this.items.reduce(
        (amount: number, current: { amount: number; discount: number; shipping: number }) => {
          const discountNegative = -(Math.sign(current.discount) === 1 ? current.discount : -current.discount);
          return amount + (current.amount + discountNegative + current.shipping);
        },
        0,
      );
    }
    const discountValue = this.myForm.get('discounts')?.value;
    const discount = -(Math.sign(discountValue) === 1 ? discountValue : -discountValue);
    const shipping = Number(this.myForm.get('shipping')?.value);
    const fee = Number(this.myForm.get('fee')?.value);
    return total + discount + shipping + fee;
  }

  newMinicartItem(): any {
    const discountValue = this.minicartItems.get('discount')?.value;
    return {
      name: this.minicartItems.get('name')?.value,
      quantity: this.minicartItems.get('quantity')?.value,
      unitAmount: +this.minicartItems.get('amount')?.value,
      amount: +this.minicartItems.get('amount')?.value * this.minicartItems.get('quantity')?.value,
      sku: this.minicartItems.get('sku')?.value,
      category: this.minicartItems.get('category')?.value,
      discount: -(Math.sign(discountValue) === 1 ? discountValue : -discountValue),
      shipping: Number(this.minicartItems.get('shipping')?.value),
    };
  }

  addMinicartItem(): void {
    this.items.push(this.newMinicartItem());
    this.myForm.get('amount')?.setValue(this.calculateTotal());
  }

  removeMinicartItem(i: number): void {
    this.items.splice(i, 1);
    this.myForm.get('amount')?.setValue(this.calculateTotal());
  }

  private teardownForm(): void {
    try {
      this.epagoFormInstance?.unmount();
    } catch {
      /* noop */
    }
    this.epagoFormInstance = null;
  }

  private clearContainer(el: ElementRef | undefined): void {
    if (el?.nativeElement) {
      this.renderer.setProperty(el.nativeElement, 'innerHTML', '');
    }
  }

  private mapEnvToSdk(env: string): string {
    if (env === 'QAnL&F' || env === 'QAV1' || env === 'PROD/QA' || env === 'SDK_VERSION_OLD') return 'quality';
    if (env === 'PRODCORD' || env === 'PROD') return 'production';
    return 'quality';
  }

  /**
   * Misma forma que `form-checkout2` / PayLink: una sola `transaction` con `order` + `settings`.
   * El SDK React arma la firma y el `POST orders/create` por dentro (`secretKey` a nivel config).
   */
  private buildQrTransactionPayload(): { order: Record<string, unknown>; settings: Record<string, unknown> } {
    const discountValue = this.myForm.get('discounts')?.value;
    const amount = this.calculateTotal();
    const currencyCode = this.myForm.get('currency')?.value;
    const orderNumber = this.myForm.get('orderId')?.value;

    return {
      order: {
        number: orderNumber,
        type: 'ORDER',
        integrationMode: this.myForm.get('integrationMode')?.value ?? null,
        country: { code: 'PER' },
        currency: { code: currencyCode },
        merchantStoreCode: this.myForm.get('merchantStoreCode')?.value,
        amountEntryIndicator: false,
        amount,
        discounts: -(Math.sign(discountValue) === 1 ? discountValue : -discountValue),
        taxes: Number(this.myForm.get('taxes')?.value),
        fee: Number(this.myForm.get('fee')?.value),
        shippingAmount: Number(this.myForm.get('shipping')?.value),
        dispatch: 'HOME_DELIVERY',
        products: [...this.items],
        multipleCustomerIndicator: false,
        customer: {
          identifier: this.myForm.get('identifier')?.value,
          name: this.myForm.get('name')?.value,
          lastName: this.myForm.get('lastname')?.value,
          phone: this.myForm.get('phone')?.value,
          email: this.myForm.get('email')?.value,
          document: {
            type: this.myForm.get('documentType')?.value,
            number: this.myForm.get('documentNumber')?.value,
          },
          address: {
            country: this.myForm.get('addressCountry')?.value,
            levels: [
              this.myForm.get('addressLevel1')?.value,
              this.myForm.get('addressLevel2')?.value,
              this.myForm.get('addressLevel3')?.value,
            ],
            line1: this.myForm.get('address1')?.value,
            zip: this.myForm.get('addressZip')?.value,
          },
        },
        metadata: [],
      },
      settings: {
        expiration: {
          type: 'DATE',
          date: this.myForm.get('expirationDate')?.value,
        },
        paymentMethod: 'AGORA',
        language: 'es_PE',
      },
    };
  }

  async generatePayment(htmlDiv: '#cart-container' | '#cart-container-2'): Promise<void> {
    this.loadError = null;
    this.loadingSdk = true;
    this.teardownForm();

    const sdkUrl = this.scriptService.normalizeReactSdkUrl(EPAGO_REACT_SDK_UMD_URL_QAS);

    try {
      this.scriptService.ensureReactSdkStylesheet(sdkUrl);
      const useEsm =
        /\.es\.js(\?|$)/i.test(sdkUrl) ||
        /\/epago-sdk\.es\.js/i.test(sdkUrl) ||
        /\/src\/index\.tsx?(\?|$)/i.test(sdkUrl);
      let FormCtor: new (config: unknown) => EpagoFormInstance;

      if (useEsm) {
        const mod = await this.scriptService.loadReactSdkEsmModule(sdkUrl);
        const ctor = mod.Form ?? mod.default?.Form;
        if (!ctor) {
          throw new Error('El módulo ESM no exporta Form. Revisa epago-sdk.es.js.');
        }
        FormCtor = ctor as new (config: unknown) => EpagoFormInstance;
      } else {
        await this.scriptService.loadReactSdkUmdScript(sdkUrl);
        FormCtor = this.scriptService.getReactSdkFormFromGlobal() as new (config: unknown) => EpagoFormInstance;
      }

      const env = this.myForm.get('env')?.value;
      const environmentValue = this.mapEnvToSdk(env);
      const identifier = this.myForm.get('merchantKey')?.value;
      const secretKey = this.myForm.get('merchantToken')?.value;

      const epagoFormConfig: Record<string, unknown> = {
        qrPayment: true,
        authentication: {
          identifier,
        },
        secretKey,
        transaction: this.buildQrTransactionPayload(),
        environment: environmentValue,
        listeners: {
          afterPay: (response: unknown) => {
            console.info({ reactSdkQrAfterPay: response });
          },
        },
      };

      if (htmlDiv === '#cart-container') {
        this.clearContainer(this.cartContainer);
      } else {
        this.clearContainer(this.cartContainer2);
      }

      const form = new FormCtor(epagoFormConfig);
      this.epagoFormInstance = form;
      form.render(htmlDiv);

      if (htmlDiv === '#cart-container') {
        this.dialog?.nativeElement.showModal();
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      this.loadError = `Error cargando o iniciando el SDK: ${msg}`;
      console.error(e);
    } finally {
      this.loadingSdk = false;
    }
  }

  closePaymentWindow(): void {
    this.teardownForm();
    this.dialog?.nativeElement.close();
  }

  copyOrderId(): void {
    const id = this.myForm.get('orderId')?.value;
    if (id == null || id === '') {
      return;
    }
    navigator.clipboard?.writeText(String(id)).catch(() => undefined);
  }
}
